<template>
  <div
    class="app-dialer-call-card-avatars"
    :class="{ [`is-${size}`]: true }"
  >
    <contact-avatar
      v-if="isContactVisible"
      :person="contact"
      :avatar="avatar"
    />

    <agent-avatar
      v-if="showAgentAvatar"
      :person="agent"
      :avatar="avatar"
    />

    <component
      :is="getAvatarType(member)"
      v-for="member of visibleAvatars"
      :key="member.memberId"
      :person="getPerson(member)"
      :avatar="avatar"
    />

    <div
      v-if="additionalAvatarsLabel"
      class="additional-avatars"
      :class="{ rounded: avatar.rounded }"
    >
      {{ additionalAvatarsLabel }}
    </div>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue';
import { defineComponent } from 'vue-demi';

import { AgentAvatar, ContactAvatar } from '@onereach/hitl-base-avatars';
import { ConferenceMember, CALL_MEMBER_TYPE } from '../../types';
import { useDialerMembers } from '../../composables';
import {
  AVATAR_PROP,
  AGENT_PROP,
  CONTACT_PROP,
  MEMBERS_PROP,
  SIZE_PROP,
} from '../../constants';

export default defineComponent({
  name: 'AppDialerCallCardAvatars',
  components: {
    AgentAvatar,
    ContactAvatar,
  },
  props: {
    avatar: AVATAR_PROP,
    agent: AGENT_PROP,
    contact: CONTACT_PROP,
    members: MEMBERS_PROP,
    size: SIZE_PROP,
  },
  setup() {
    const { getPerson } = useDialerMembers();
    return { getPerson, MAX_VISIBLE_AVATARS: 4 };
  },
  computed: {
    maxVisibleAvatars(): number {
      const DESKTOP_VISIBLE_AVATARS = 4;
      const MOBILE_VISIBLE_AVATARS = 1;
      return this.size === 'mobile'
        ? MOBILE_VISIBLE_AVATARS
        : DESKTOP_VISIBLE_AVATARS;
    },

    showAgentAvatar(): boolean {
      /* Agent avatar is displayed first when no contact is present */
      if (!this.contact) {
        return true;
      }

      /* If we can show at least two avatars */
      return this.maxVisibleAvatars > 1;
    },

    /** Other members' avatars are placed after primary slots */
    additionalAvatars(): ConferenceMember[] {
      return this.members.filter(member => {
        return member.memberId !== this.agent.id
          && member.memberId !== this.contact?.contactId;
      });
    },

    /** Contact and agent avatars take first two avatar slots */
    nPriorityAvatars(): number {
      return this.isContactVisible ? 2 : 1;
    },

    isContactVisible(): boolean {
      return this.contact && this.members.some(member => member.memberId === this.contact.contactId);
    },

    /** Other member's avatars will be displayed if there are any slots left */
    visibleAvatars(): ConferenceMember[] {
      /* Contact + agent or only agent avatars */
      const nAvailableSlots = this.maxVisibleAvatars - this.nPriorityAvatars;
      const visible = Math.max(nAvailableSlots, 0);
      return this.additionalAvatars.slice(0, visible);
    },

    additionalAvatarsLabel(): string {
      const nAvatars = this.nPriorityAvatars + this.additionalAvatars.length;
      if (nAvatars <= this.maxVisibleAvatars) {
        return '';
      }

      const additional = nAvatars - this.maxVisibleAvatars;
      return additional > 0 ? `+${additional}` : '';
    },
  },
  methods: {
    getAvatarType(member: ConferenceMember): Component {
      return member.type === CALL_MEMBER_TYPE.AGENT
        ? AgentAvatar
        : ContactAvatar;
    },
  },
});
</script>

<style lang="scss" scoped>
@import '../../styles';

.app-dialer-call-card-avatars {
  @include app-dialer-avatar-overrides-inverse;
  --avatar-height: 2.5rem;
  --additional-height: 2.75rem;
  --additional-font-size: var(--fs-1);

  display: flex;

  &.is-mobile {
    --avatar-height: 3.5rem;
    --additional-height: 3.75rem;
    --additional-font-size: var(--fs-2);
  }

  .agent-avatar,
  .contact-avatar {
    height: var(--avatar-height) !important;
    width: var(--avatar-height) !important;
  }

  .contact-avatar:nth-child(n + 2),
  .agent-avatar:nth-child(n + 2),
  .additional-avatars {
    margin-left: -0.75rem;
  }

  .additional-avatars {
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--additional-height);
    width: var(--additional-height);
    font-size: var(--additional-font-size);
    font-weight: var(--fw-1);
    background: var(--or-c-outline);
    border: 0.125rem solid var(--or-c-inverse-surface);

    &.rounded {
      border-radius: 50%;
    }
  }
}
</style>
